import { FormEvent, useContext, useState } from 'react';

import crossDark from '../../../assets/images/icons/cross-dark.svg';
import CommentData from '../../../types/commentData';
import { AuthContext } from '../../../utils/AuthProvider';
import IconButton from '../../atoms/button/IconButton';
import Input from '../../atoms/input';
import AnonymousRoundedImage from '../../atoms/rounded-image/AnonymousRoundedImage';
import UserRoundedImage from '../../atoms/rounded-image/UserRoundedImage';

import './CommentForm.scss';

const INPUT_PROPS = {
  autoFocus: false,
  autoComplete: 'off',
  id: 'comment',
  required: true,
  type: 'text',
};

interface CommentInputProps {
  onSend?: (commentBody: string, isAnonymous: boolean) => void;
  onClearReplyingToComment?: () => void;
  placeholder?: string;
  replyToComment?: CommentData;
  className?: string;
}

export default function CommentForm({
  onSend,
  onClearReplyingToComment,
  placeholder = '',
  replyToComment,
  className = '',
}: CommentInputProps) {
  // States
  const [commentString, setCommentString] = useState<string>('');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const { userProfile } = useContext(AuthContext)!;

  // Callbacks
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSend && onSend(commentString, isAnonymous);
    setCommentString('');
    onClearReplyingToComment && onClearReplyingToComment();
  }

  return (
    <form className={`comment-form ${className}`} onSubmit={onSubmit}>
      {replyToComment && (
        <div className="reply-row mb-2">
          <span className="mr-1">Replying to</span>
          <span className="reply-to-comment mr-2">{replyToComment.body}</span>
          <button onClick={onClearReplyingToComment} type="button">
            <img className="close-icon" src={crossDark} alt="Cross" />
          </button>
        </div>
      )}

      <div className="flex relative items-center gap-3 mb-2">
        {isAnonymous && <p className="anonymous-hint">Asking anonymously</p>}
        {userProfile && (
          <button onClick={() => setIsAnonymous(!isAnonymous)} type="button">
            {isAnonymous ? (
              <AnonymousRoundedImage className="shrink-0" />
            ) : (
              <UserRoundedImage className="shrink-0" userProfile={userProfile} />
            )}
          </button>
        )}
        <Input
          className="input flex-grow"
          {...{ ...INPUT_PROPS, placeholder }}
          state={[commentString, setCommentString]}
        />
        <IconButton icon="send-outline" type="submit" />
      </div>
      <p className="text-grey-sm text-center">
        {isAnonymous ? 'Click profile picture to ask as you' : 'Click profile picture to ask anonymously'}
      </p>
    </form>
  );
}
